import { signIn, logOutServer, validateTravelAccountCredentials } from '@/expressway-api/auth.api'
import { User, UserSession } from '@/models/User'
import { computed, onMounted, onUnmounted, ref, watch, readonly } from 'vue'

const USER_SESSION_KEY = 'USER_SESSION_KEY'

export const getUserSessionStorage = (): UserSession | undefined => {
  const sessionString = localStorage.getItem(USER_SESSION_KEY)
  return sessionString ? JSON.parse(sessionString) : null
}

// Treat a session as lapsed slightly early so a request started right on the boundary
// does not go out with a token the API has already dropped.
const EXPIRY_SKEW_MS = 5000

export const isSessionExpired = (session = getUserSessionStorage()): boolean => {
  if (!session?.ExpiresAt) return false

  return Date.now() >= session.ExpiresAt - EXPIRY_SKEW_MS
}

export const setUserSessionStorage = (userSession?: UserSession) => {
  if (userSession) {
    localStorage.setItem(USER_SESSION_KEY, JSON.stringify(userSession))
  } else localStorage.removeItem(USER_SESSION_KEY)
}

const userSession = ref<UserSession | undefined>(getUserSessionStorage())

// Drops the session from storage and from the reactive ref, so isLoggedIn and anything
// derived from it update too. Usable outside a component, unlike useSessions().logOut.
export const clearUserSession = () => {
  setUserSessionStorage()
  userSession.value = undefined
}

const storageListener = (event: StorageEvent) => {
  if (event.key === USER_SESSION_KEY) {
    userSession.value = getUserSessionStorage()
  }
}

export default function useSessions () {
  const isLoggedIn = computed(() => !!userSession.value)
  const logIn = (user: User) =>
    signIn(user).then(response => {
      userSession.value = {
        ...response.data,
        Username: user.Username,
        ExpiresAt: Date.now() + response.data.ExpiresIn * 1000
      }
      return userSession.value
    })
  // Only the session: a lapsed session keeps the basket so the purchase can carry on
  // after signing back in. Signing out on purpose goes through useSignOut instead.
  const logOut = (localOnly = true) => {
    const accessToken = userSession.value?.AccessToken
    clearUserSession()
    if (!localOnly && accessToken) logOutServer(accessToken).catch(() => undefined)
  }

  watch(userSession, () => {
    setUserSessionStorage(userSession.value)
  })

  onMounted(() => {
    window.addEventListener('storage', storageListener)
  })
  onUnmounted(() => {
    window.removeEventListener('storage', storageListener)
  })

  const verifyPassword = async (user: User) =>
    await validateTravelAccountCredentials(user).then(() => true)
      .catch(() => false)

  return {
    verifyPassword,
    userSession: readonly(userSession),
    isLoggedIn,
    logIn,
    logOut
  }
}
